/**
 * ResultsGrid Component
 *
 * Grid display of search results with listing cards.
 */

import React from 'react';
import { ListingCard } from '../../../components/listings/ListingCard';
import type { ListingResult } from '../hooks/useSearchResults';

interface ResultsGridProps {
  listings: ListingResult[];
  isLoading: boolean;
  onSave?: (listingId: string) => void;
  savedIds?: string[];
}

export const ResultsGrid: React.FC<ResultsGridProps> = ({
  listings,
  isLoading,
  onSave,
  savedIds = [],
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <ListingCardSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (listings.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <SearchIcon className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No results found</h3>
        <p className="text-gray-500 max-w-md mx-auto">
          Try adjusting your search criteria or removing some filters to see more results.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {listings.map((listing) => (
        <ListingCard
          key={listing.id}
          listing={listing}
          onSave={onSave}
          isSaved={savedIds.includes(listing.id)}
        />
      ))}
    </div>
  );
};

const ListingCardSkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="aspect-[4/3] bg-gray-200 rounded-xl mb-3" />
    <div className="space-y-2">
      <div className="h-4 bg-gray-200 rounded w-3/4" />
      <div className="h-3 bg-gray-200 rounded w-1/2" />
      <div className="h-3 bg-gray-200 rounded w-1/4" />
    </div>
  </div>
);

const SearchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

export default ResultsGrid;
